import type { Dispatch, ReactNode } from 'react';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';

import { selectAuthToken } from 'reduxLocal/slices/authSlice';

import { setAxiosAuthToken } from 'api/utils';

type AuthContextProps = {
  accessToken?: string;
  setAccessToken: Dispatch<string | undefined>;
};

const AuthContext = createContext({} as AuthContextProps);

type Props = {
  children: ReactNode;
};

/**
 * Context provider managing auth in our application
 */
export const AuthProvider = ({ children }: Props) => {
  const [token, setAccessToken] = useState<string | undefined>();

  /**
   * Currently Redux / Redux Query is still managing most auth,
   * such as sign-in / sign-out / access tokens on initial render,
   * therefore we still need to access the access token at the start
   */
  const reduxAccessToken = useSelector(selectAuthToken);
  const accessToken = useMemo(
    () => (token || reduxAccessToken) ?? undefined,
    [token, reduxAccessToken]
  );

  /**
   * Effect updating the Axios auth token
   * Layout effect so the header is updated before any child's passive effect
   * fires a request in the same commit (e.g. swapping the login token for the
   * dashboard token and navigating to /login_transition in one render)
   */
  useLayoutEffect(() => {
    setAxiosAuthToken(accessToken);
  }, [accessToken]);

  return (
    <AuthContext.Provider value={{ accessToken, setAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
